import { useMemo } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Empty } from 'antd';
import {
  Drawer,
  Table,
  Button,
  SkeletonLoader,
  Status,
  Modal,
} from '@insight-health-ai/roseate/components';
import {
  useGetBulkAppointmentUploadHistory,
  useDownloadAppointmentCSV,
} from 'src/hooks/appointment';
import { AppointmentSyncRecord } from 'src/models';
import { ColumnsType } from 'antd/es/table';
import {
  ChevronDown,
  ChevronUp,
  Download01,
  AlertCircle,
  CheckCircle,
  MinusCircle,
} from '@insight-health-ai/roseate/icons';

dayjs.extend(relativeTime);

type AppointmentBulkUploadHistoryDrawerProps = {
  open: boolean;
  onClose: () => void;
};

type DownloadButtonProps = {
  record: AppointmentSyncRecord;
};

const DownloadButton = ({ record }: DownloadButtonProps) => {
  const { mutateAsync: downloadCSV, isPending } = useDownloadAppointmentCSV();

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Download CSV',
      content: 'Are you sure you want to download the CSV file?',
      primaryAction: {
        onClick: () =>
          downloadCSV({
            s3_key: record.s3_key,
            bucket_name: record.bucket_name,
          }).then((data) => {
            if (data.download_url) {
              window.open(data.download_url, '_blank');
            }
          }),
      },
    });
  };

  return (
    <Button
      compact
      variant="outlined"
      size="sm"
      icon={<Download01 />}
      loading={isPending}
      onClick={handleDownload}
    >
      Download
    </Button>
  );
};

const columns: ColumnsType<AppointmentSyncRecord> = [
  {
    title: 'Upload date',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 200,
    render: (value: string, record) => {
      const date = dayjs(value);
      return (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-gray-900">
            {date.format('MMM D, YYYY h:mm A')}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs">{date.fromNow()}</span>
            <DownloadButton record={record} />
          </div>
        </div>
      );
    },
  },
  {
    title: 'Uploaded by',
    dataIndex: 'created_by',
    key: 'created_by',
    width: 250,
    render: (value: string) => <span className="text-gray-700">{value}</span>,
  },
  {
    title: 'Upload status',
    key: 'status',
    render: (_, record) => {
      return (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
          <Status
            variant="text"
            appearance="success"
            size="sm"
            icon={<CheckCircle />}
            label={`Created: ${record.created}`}
          />
          <Status
            variant="text"
            appearance="success"
            size="sm"
            icon={<CheckCircle />}
            label={`Updated: ${record.updated}`}
          />
          <Status
            variant="text"
            appearance="info"
            size="sm"
            icon={<CheckCircle />}
            label={`Aura Only: ${record.intake_skipped}`}
          />
          <Status
            variant="text"
            appearance="error"
            size="sm"
            icon={<AlertCircle />}
            label={`Skipped: ${record.skipped}`}
          />
          <Status
            variant="text"
            appearance="neutral"
            size="sm"
            icon={<MinusCircle />}
            label={`Unchanged: ${record.unchanged}`}
          />
        </div>
      );
    },
  },
  // @ts-expect-error - EXPAND_COLUMN is not supported in the Table component
  Table.EXPAND_COLUMN,
];

export const AppointmentBulkUploadHistoryDrawer = ({
  open,
  onClose,
}: AppointmentBulkUploadHistoryDrawerProps) => {
  const {
    data: bulkAppointmentUploadHistory,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useGetBulkAppointmentUploadHistory(
    { perPage: 10 },
    {
      enabled: open,
    }
  );

  const data = useMemo(() => {
    return bulkAppointmentUploadHistory?.pages.flatMap(
      (page) => page.histories
    );
  }, [bulkAppointmentUploadHistory]);

  const total = bulkAppointmentUploadHistory?.pages[0]?.total ?? 0;

  return (
    <Drawer
      size="xl"
      title="Bulk upload history"
      className="insight-background"
      open={open}
      onClose={onClose}
    >
      <Table
        data={data}
        columns={columns}
        loading={isLoading}
        // @ts-expect-error - rowKey is not supported in the Table component
        rowKey="history_id"
        pagination={false}
        emptyText={
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No upload history found"
          />
        }
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) =>
            record.verbose_errors?.length > 0 ? (
              <Button
                onClick={(e) => onExpand(record, e)}
                variant="text"
                appearance="secondary"
                size="sm"
                icon={{
                  position: 'end',
                  icon: expanded ? <ChevronUp /> : <ChevronDown />,
                }}
              >
                {expanded ? 'Hide' : 'View'} details
              </Button>
            ) : null,
          expandedRowRender: (record) => {
            if (!record.verbose_errors?.length) {
              return null;
            }
            return (
              <div className="py-3 px-4 bg-red-50 border-l-4 border-red-400 rounded-r">
                <div className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {record.verbose_errors.length} error
                  {record.verbose_errors.length > 1 ? 's' : ''} found
                </div>
                <div className="flex flex-col gap-1">
                  {record.verbose_errors.map((error, index) => (
                    <div
                      key={index}
                      className="text-sm text-red-700 py-1 border-b border-red-200 last:border-b-0"
                    >
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            );
          },
        }}
        footer={() => {
          if (!data?.length) {
            return null;
          }

          return (
            <div className="flex flex-col p-3 gap-2 items-center justify-center">
              {isFetching ? (
                <SkeletonLoader
                  active
                  paragraph={{ rows: 1, width: 100 }}
                  title={false}
                  style={{ width: 100 }}
                />
              ) : (
                <div className="text-sm">
                  Showing {data?.length} of {total}
                </div>
              )}
              {hasNextPage && (
                <Button
                  loading={isFetchingNextPage}
                  onClick={() => {
                    fetchNextPage();
                  }}
                >
                  Show more
                </Button>
              )}
            </div>
          );
        }}
      />
    </Drawer>
  );
};
